import React, { useState, useRef, useEffect, useCallback } from 'react';

// Dark galactic backdrop — procedural gradient (no copyrighted image).
// A procedural fallback always renders; no external image dependency.

const LOCATIONS = [
  // ═══ Segmentum Solar — the heart of Imperium ═══
  { id: 'terra', name: 'Holy Terra', x: 50, y: 50, region: 'Segmentum Solar', major: true },
  { id: 'mars', name: 'Mars', x: 51, y: 50, region: 'Segmentum Solar', major: true },
  { id: 'luna', name: 'Luna', x: 49, y: 50, region: 'Segmentum Solar' },
  { id: 'segmentum_solar', name: 'Segmentum Solar', x: 48, y: 52, region: 'Segmentum Solar' },

  // ═══ Segmentum Obscurus — northern galactic ═══
  { id: 'cadia', name: 'Cadia (Shattered)', x: 58, y: 22, region: 'Segmentum Obscurus', major: true },
  { id: 'eye_of_terror', name: 'Eye of Terror', x: 62, y: 18, region: 'Segmentum Obscurus', major: true },
  { id: 'cypra_mundi', name: 'Cypra Mundi', x: 46, y: 22, region: 'Segmentum Obscurus' },
  { id: 'fenris', name: 'Fenris', x: 40, y: 24, region: 'Segmentum Obscurus', major: true },
  { id: 'prospero', name: 'Prospero (Ashen)', x: 44, y: 28, region: 'Segmentum Obscurus' },
  { id: 'caliban', name: 'Caliban (Sundered)', x: 56, y: 30, region: 'Segmentum Obscurus' },
  { id: 'medrengard', name: 'Medrengard', x: 66, y: 20, region: 'Segmentum Obscurus' },
  { id: 'sicarus', name: 'Sicarus (Daemon World)', x: 64, y: 22, region: 'Segmentum Obscurus' },

  // ═══ Segmentum Ultima — eastern galactic ═══
  { id: 'macragge', name: 'Macragge', x: 78, y: 48, region: 'Segmentum Ultima', major: true },
  { id: 'ultramar', name: 'Ultramar', x: 76, y: 50, region: 'Segmentum Ultima' },
  { id: 'baal', name: 'Baal', x: 72, y: 42, region: 'Segmentum Ultima', major: true },
  { id: 'damocles_gulf', name: 'Damocles Gulf', x: 86, y: 58, region: 'Segmentum Ultima' },
  { id: "t'au", name: "T'au", x: 90, y: 62, region: 'Segmentum Ultima', major: true },
  { id: 'biel_tan', name: 'Biel-Tan', x: 82, y: 52, region: 'Segmentum Ultima' },
  { id: 'iyanden', name: 'Iyanden', x: 84, y: 46, region: 'Segmentum Ultima' },
  { id: 'vigilus', name: 'Vigilus', x: 80, y: 40, region: 'Segmentum Ultima' },
  { id: 'segmentum_ultima', name: 'Segmentum Ultima', x: 84, y: 50, region: 'Segmentum Ultima' },

  // ═══ Segmentum Tempestus — southern galactic ═══
  { id: 'armageddon', name: 'Armageddon', x: 60, y: 76, region: 'Segmentum Tempestus', major: true },
  { id: 'catachan', name: 'Catachan', x: 56, y: 80, region: 'Segmentum Tempestus' },
  { id: 'krieg', name: 'Krieg', x: 52, y: 78, region: 'Segmentum Tempestus' },
  { id: 'necromunda', name: 'Necromunda', x: 48, y: 74, region: 'Segmentum Tempestus' },
  { id: 'rynn', name: "Rynn's World", x: 44, y: 76, region: 'Segmentum Tempestus' },
  { id: 'segmentum_tempestus', name: 'Segmentum Tempestus', x: 52, y: 82, region: 'Segmentum Tempestus' },

  // ═══ Segmentum Pacificus — western galactic ═══
  { id: 'segmentum_pacificus', name: 'Segmentum Pacificus', x: 16, y: 50, region: 'Segmentum Pacificus' },
  { id: 'ulthwe', name: 'Ulthwé', x: 20, y: 40, region: 'Segmentum Pacificus', major: true },
  { id: 'saim_hann', name: 'Saim-Hann', x: 18, y: 60, region: 'Segmentum Pacificus' },
  { id: 'mordian', name: 'Mordian', x: 26, y: 48, region: 'Segmentum Pacificus' },
  { id: 'tallarn', name: 'Tallarn', x: 22, y: 54, region: 'Segmentum Pacificus' },

  // ═══ Ancient / legendary ═══
  { id: 'isstvan', name: 'Isstvan System', x: 68, y: 30, region: 'Segmentum Obscurus' },
  { id: 'calth', name: 'Calth (Scoured)', x: 74, y: 44, region: 'Segmentum Ultima' },
  { id: 'commorragh', name: 'Commorragh (Webway)', x: 10, y: 36, region: 'Webway', major: true },
];

export { LOCATIONS };

function parseYear(position) {
  if (!position) return null;
  const p = String(position).trim().toUpperCase().replace(/\s+/g, '');
  try {
    if (p.includes('.') && p.includes('M')) {
      const [yearPart, millPart] = p.split('.');
      const mill = parseInt(millPart.replace('M', ''), 10);
      const yearInMill = parseInt(yearPart, 10);
      return (mill - 1) * 1000 + yearInMill;
    }
    const n = parseInt(p, 10);
    return Number.isNaN(n) ? null : n;
  } catch (e) { return null; }
}

// Era-aware faction territories (viewBox 0-100).
function getFactionsForYear(year) {
  if (year === null || year === undefined) year = 40999;

  // Shared xenos presences common to most eras
  const orks = { id: 'orks', label: 'ORKS', color: '34,197,94', // waaagh green
    path: 'M55,73 L66,72 L72,80 L62,85 L54,82 Z',
    textX: 62, textY: 79, fontSize: 1.3, dashed: true };

  const tyranids = { id: 'tyranids', label: 'HIVE FLEETS', color: '190,242,100', // chitin green-yellow
    path: 'M82,68 L96,66 L98,86 L88,90 L80,82 Z',
    textX: 89, textY: 78, fontSize: 1.1, dashed: true };

  if (year < 30000) {
    // Pre-Great Crusade era (rarely shown) — Imperium not yet formed
    return [
      { id: 'great-crusade', label: 'GREAT CRUSADE', color: '250,204,21',
        path: 'M28,30 L76,28 L76,72 L28,72 Z',
        textX: 52, textY: 50, fontSize: 1.8 },
    ];
  }

  if (year >= 30005 && year <= 30021) {
    // ═══ Horus Heresy (M31) — Imperium torn in two ═══
    return [
      { id: 'loyalists', label: 'LOYALIST IMPERIUM', color: '250,204,21', // gold
        path: 'M28,30 L58,28 L56,72 L28,72 Z',
        textX: 42, textY: 50, fontSize: 1.5 },
      { id: 'traitors', label: 'TRAITOR LEGIONS', color: '185,28,28', // heresy red
        path: 'M56,28 L80,28 L80,72 L58,72 Z',
        textX: 68, textY: 50, fontSize: 1.5 },
      { id: 'eye-nascent', label: 'EYE OF TERROR', color: '147,51,234', // warp purple
        path: 'M58,14 L72,14 L72,22 L58,22 Z',
        textX: 65, textY: 18, fontSize: 1.2, dashed: true },
    ];
  }

  if (year >= 30022 && year <= 40899) {
    // ═══ Age of Imperium (M32-mid M41) — dominant but beset by xenos ═══
    return [
      { id: 'imperium', label: 'IMPERIUM OF MAN', color: '250,204,21',
        path: 'M22,28 L78,26 L78,74 L22,74 Z',
        textX: 50, textY: 50, fontSize: 1.9 },
      { id: 'eye-terror', label: 'EYE OF TERROR', color: '147,51,234',
        path: 'M58,14 L72,14 L72,22 L58,22 Z',
        textX: 65, textY: 18, fontSize: 1.2, dashed: true },
      { id: 'craftworlds', label: 'ELDAR CRAFTWORLDS', color: '147,197,253', // wraithbone blue
        path: 'M12,34 L28,34 L28,64 L12,64 Z',
        textX: 20, textY: 48, fontSize: 1.0, dashed: true },
      orks, tyranids,
    ];
  }

  if (year >= 40900 && year <= 40999) {
    // ═══ Late M41 — "Time of Ending" — Imperium besieged on all sides ═══
    return [
      { id: 'imperium-late', label: 'IMPERIUM OF MAN', color: '250,204,21',
        path: 'M26,32 L74,30 L74,70 L26,70 Z',
        textX: 50, textY: 50, fontSize: 1.8 },
      { id: 'eye-terror-late', label: 'EYE OF TERROR', color: '147,51,234',
        path: 'M56,12 L74,12 L74,24 L56,24 Z',
        textX: 65, textY: 18, fontSize: 1.3 },
      { id: 'craftworlds-late', label: 'ELDAR CRAFTWORLDS', color: '147,197,253',
        path: 'M12,34 L28,34 L28,64 L12,64 Z',
        textX: 20, textY: 48, fontSize: 1.0, dashed: true },
      { id: 'tau-sphere', label: "T'AU EMPIRE", color: '59,130,246', // tau blue
        path: 'M85,56 L96,56 L96,68 L85,68 Z',
        textX: 90, textY: 62, fontSize: 1.1, dashed: true },
      { id: 'necrons', label: 'NECRON AWAKENINGS', color: '209,213,219', // silver
        path: 'M38,82 L62,82 L62,90 L38,90 Z',
        textX: 50, textY: 87, fontSize: 1.0, dashed: true, dim: true },
      orks, tyranids,
    ];
  }

  // ═══ Indomitus Era (M42+, year >= 41000) — Great Rift has bisected the galaxy ═══
  return [
    { id: 'imperium-sanctus', label: 'IMPERIUM SANCTUS', color: '250,204,21',
      path: 'M26,50 L74,48 L74,74 L26,74 Z',
      textX: 50, textY: 64, fontSize: 1.6 },
    { id: 'imperium-nihilus', label: 'IMPERIUM NIHILUS', color: '163,163,163', // cold grey — cut off
      path: 'M26,28 L74,26 L74,48 L26,48 Z',
      textX: 50, textY: 38, fontSize: 1.5, dashed: true, dim: true },
    // Great Rift — diagonal slash across the galaxy
    { id: 'great-rift', label: 'THE GREAT RIFT', color: '147,51,234',
      path: 'M14,48 L88,46 L88,50 L14,52 Z',
      textX: 50, textY: 47, fontSize: 1.1 },
    { id: 'eye-terror-m42', label: 'EYE OF TERROR', color: '147,51,234',
      path: 'M56,12 L74,12 L74,26 L56,26 Z',
      textX: 65, textY: 19, fontSize: 1.3 },
    { id: 'craftworlds-m42', label: 'YNNARI / CRAFTWORLDS', color: '147,197,253',
      path: 'M12,34 L28,34 L28,64 L12,64 Z',
      textX: 20, textY: 48, fontSize: 0.95, dashed: true },
    { id: 'tau-sphere-m42', label: "T'AU EMPIRE", color: '59,130,246',
      path: 'M85,56 L96,56 L96,68 L85,68 Z',
      textX: 90, textY: 62, fontSize: 1.1, dashed: true },
    { id: 'necrons-m42', label: 'NECRON DYNASTIES', color: '209,213,219',
      path: 'M38,80 L68,80 L68,90 L38,90 Z',
      textX: 53, textY: 86, fontSize: 1.1, dashed: true },
    orks, tyranids,
  ];
}

export default function WarhammerMap({ characterLocation, onLocationClick, worldCharacters = [], events = [], timelinePosition = '' }) {
  const containerRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [pulsePhase, setPulsePhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setPulsePhase(p => (p + 1) % 100), 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (characterLocation && containerRef.current) {
      const loc = LOCATIONS.find(l =>
        l.id === characterLocation || l.name.toLowerCase() === characterLocation?.toLowerCase()
      );
      if (loc) {
        const rect = containerRef.current.getBoundingClientRect();
        const targetZoom = 2;
        setZoom(targetZoom);
        setOffset({
          x: -(loc.x / 100 * rect.width * targetZoom - rect.width / 2),
          y: -(loc.y / 100 * rect.height * targetZoom - rect.height / 2),
        });
      }
    }
  }, [characterLocation]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.5, Math.min(6, zoom * delta));
    const scale = newZoom / zoom;
    setOffset(prev => ({
      x: mouseX - scale * (mouseX - prev.x),
      y: mouseY - scale * (mouseY - prev.y),
    }));
    setZoom(newZoom);
  }, [zoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.location-dot')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging || !dragStart) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => { setDragging(false); setDragStart(null); };

  const charLoc = characterLocation ? LOCATIONS.find(l =>
    l.id === characterLocation || l.name.toLowerCase() === characterLocation?.toLowerCase()
  ) : null;

  const pulseScale = 1 + Math.sin(pulsePhase * 0.12) * 0.4;
  const pulseOpacity = 0.5 + Math.sin(pulsePhase * 0.08) * 0.3;

  const year = parseYear(timelinePosition);
  const factions = getFactionsForYear(year);

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden relative"
      style={{ cursor: dragging ? 'grabbing' : 'grab', backgroundColor: '#060206' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="warhammer-map"
    >
      <div
        className="absolute will-change-transform"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: '100%',
          height: '100%',
          transition: dragging ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        {/* Grimdark galaxy — procedural warp-storm background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, #2a0a1a 0%, #120208 50%, #060206 100%)',
            backgroundImage: `
              radial-gradient(circle at 50% 50%, rgba(250,204,21,0.08) 0%, transparent 8%),
              radial-gradient(circle at 65% 18%, rgba(147,51,234,0.25) 0%, transparent 14%),
              radial-gradient(circle at 20% 48%, rgba(147,197,253,0.06) 0%, transparent 10%),
              radial-gradient(circle at 90% 62%, rgba(59,130,246,0.05) 0%, transparent 8%),
              radial-gradient(circle at 60% 78%, rgba(34,197,94,0.05) 0%, transparent 10%),
              radial-gradient(circle at 35% 35%, rgba(255,255,255,0.015) 0%, transparent 45%)
            `,
          }}
        />

        {/* Star field — procedural dots */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Small background stars */}
          {Array.from({ length: 140 }).map((_, i) => {
            const sx = ((i * 17.3) % 100);
            const sy = ((i * 11.7 + 3) % 100);
            const size = 0.08 + ((i * 7) % 3) * 0.05;
            const op = 0.25 + ((i * 13) % 5) * 0.1;
            return <circle key={i} cx={sx} cy={sy} r={size} fill={`rgba(255,250,240,${op})`} />;
          })}
        </svg>

        {/* Galactic plane — subtle diagonal band of diffuse light */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(18deg, transparent 30%, rgba(200,190,160,0.04) 48%, rgba(200,190,160,0.06) 50%, rgba(200,190,160,0.04) 52%, transparent 70%)',
          }}
        />

        {/* Faction territory borders — dynamic based on era */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <filter id="wh-territory-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {factions.map((f) => {
            const opacityMul = f.dim ? 0.55 : 1;
            return (
              <g key={f.id}>
                <path
                  d={f.path}
                  fill="none"
                  stroke="rgba(0,0,0,0.75)"
                  strokeWidth={f.dashed ? 0.85 : 1.2}
                  strokeLinejoin="round"
                />
                <path
                  d={f.path}
                  fill={`rgba(${f.color},${0.12 * opacityMul})`}
                  stroke={`rgba(${f.color},${0.95 * opacityMul})`}
                  strokeWidth={f.dashed ? 0.5 : 0.75}
                  strokeDasharray={f.dashed ? '0.7 0.7' : '1.2 0.5'}
                  strokeLinejoin="round"
                  filter="url(#wh-territory-glow)"
                />
                <text
                  x={f.textX}
                  y={f.textY}
                  textAnchor="middle"
                  fill="rgba(0,0,0,0.9)"
                  stroke="rgba(0,0,0,0.9)"
                  strokeWidth={0.3}
                  fontSize={f.fontSize}
                  fontFamily="serif"
                  fontWeight="bold"
                  letterSpacing="0.2"
                >
                  {f.label}
                </text>
                <text
                  x={f.textX}
                  y={f.textY}
                  textAnchor="middle"
                  fill={`rgba(${f.color},${0.98 * opacityMul})`}
                  fontSize={f.fontSize}
                  fontFamily="serif"
                  fontWeight="bold"
                  letterSpacing="0.2"
                  filter="url(#wh-territory-glow)"
                >
                  {f.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Location markers */}
        {LOCATIONS.filter(l => l.major).map(loc => {
          const isHovered = hovered === loc.id;
          const isCharHere = charLoc?.id === loc.id;
          const dotSize = loc.major ? 6 : 3;
          const regionColor = {
            'Segmentum Solar': '#fbbf24',
            'Segmentum Obscurus': '#c084fc',
            'Segmentum Ultima': '#93c5fd',
            'Segmentum Tempestus': '#f87171',
            'Segmentum Pacificus': '#86efac',
            'Webway': '#e879f9',
          }[loc.region] || '#d1d5db';

          return (
            <div
              key={loc.id}
              className="location-dot absolute"
              style={{
                left: `${loc.x}%`,
                top: `${loc.y}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: isHovered || isCharHere ? 20 : 10,
              }}
              onMouseEnter={() => setHovered(loc.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={(e) => { e.stopPropagation(); onLocationClick?.(loc); }}
              data-testid={`location-${loc.id}`}
            >
              {isCharHere && (
                <>
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: `${20 * pulseScale}px`, height: `${20 * pulseScale}px`,
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid #fcd34d',
                    opacity: pulseOpacity * 0.6,
                  }} />
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: `${32 * pulseScale}px`, height: `${32 * pulseScale}px`,
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid #fcd34d',
                    opacity: pulseOpacity * 0.3,
                  }} />
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: '14px', height: '14px',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(252,211,77,0.35)',
                  }} />
                </>
              )}

              <div
                className="rounded-full cursor-pointer transition-all duration-150"
                style={{
                  width: `${isHovered ? dotSize + 4 : dotSize}px`,
                  height: `${isHovered ? dotSize + 4 : dotSize}px`,
                  backgroundColor: isCharHere ? '#fcd34d' : regionColor,
                  boxShadow: `0 0 ${isHovered ? 12 : loc.major ? 8 : 4}px ${regionColor}, 0 0 ${isHovered ? 20 : loc.major ? 12 : 0}px ${regionColor}`,
                  border: loc.major ? '1px solid rgba(255,255,255,0.35)' : 'none',
                }}
              />

              {(isHovered || (loc.major && zoom >= 1.2) || isCharHere) && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none"
                  style={{ bottom: `${(isHovered ? dotSize + 8 : dotSize + 4)}px` }}
                >
                  <span
                    className="px-1.5 py-0.5 rounded"
                    style={{
                      fontFamily: 'serif',
                      fontSize: isHovered ? '11px' : '9px',
                      color: isCharHere ? '#fef3c7' : isHovered ? '#ffffff' : '#fef3c7',
                      backgroundColor: isHovered ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.65)',
                      fontWeight: isCharHere ? 'bold' : 600,
                      textShadow: '0 0 4px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,0.9)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {loc.name}
                  </span>
                </div>
              )}

              {isHovered && (
                <div
                  className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-black/90 border border-amber-700/30 rounded px-2 py-1 whitespace-nowrap pointer-events-none z-50"
                  style={{ minWidth: '80px' }}
                >
                  <div className="text-[10px] font-serif italic text-amber-200/70">{loc.region}</div>
                  <div className="text-xs font-serif font-bold text-white">{loc.name}</div>
                  {isCharHere && <div className="text-[10px] font-serif text-amber-400 mt-0.5">You are here</div>}
                </div>
              )}
            </div>
          );
        })}

        {/* Event markers */}
        {events.map((evt, i) => {
          const loc = LOCATIONS.find(l =>
            l.id === evt.system_id || l.name?.toLowerCase() === evt.system_id?.toLowerCase()
          );
          if (!loc) return null;
          return (
            <div
              key={`evt-${i}`}
              className="absolute pointer-events-none"
              style={{ left: `${loc.x}%`, top: `${loc.y}%`, transform: 'translate(-50%, -50%)', zIndex: 5 }}
            >
              <div
                className="rounded-full border-2 border-dashed border-red-400"
                style={{
                  width: '22px', height: '22px',
                  opacity: 0.6 + Math.sin(pulsePhase * 0.1 + i) * 0.3,
                  boxShadow: '0 0 8px rgba(248,113,113,0.6)',
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[9px] font-serif font-bold text-red-200 whitespace-nowrap px-1 rounded"
                style={{ backgroundColor: 'rgba(0,0,0,0.75)', textShadow: '0 0 4px rgba(0,0,0,0.95)' }}
              >
                {evt.label}
              </div>
            </div>
          );
        })}

        {/* Other characters */}
        {worldCharacters.filter(c => c.location).map(char => {
          const loc = LOCATIONS.find(l =>
            l.id === char.location || l.name?.toLowerCase() === char.location?.toLowerCase()
          );
          if (!loc) return null;
          return (
            <div
              key={char.character_id}
              className="absolute pointer-events-none"
              style={{ left: `${loc.x}%`, top: `${loc.y}%`, transform: 'translate(-50%, -50%)', zIndex: 15 }}
            >
              <div
                className="w-3.5 h-3.5 rounded-full border-2 border-orange-300"
                style={{ backgroundColor: 'rgba(251,146,60,0.4)', boxShadow: '0 0 6px rgba(251,146,60,0.8)' }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[9px] font-serif font-bold text-orange-200 whitespace-nowrap px-1 rounded"
                style={{ backgroundColor: 'rgba(0,0,0,0.7)', textShadow: '0 0 4px rgba(0,0,0,0.95)' }}
              >
                {char.name}
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute bottom-3 right-3 text-[10px] font-serif italic text-amber-700/70 bg-black/50 px-2 py-1 rounded pointer-events-none">
        {(zoom * 100).toFixed(0)}%
      </div>
    </div>
  );
}
